import { Component } from '@angular/core';
import { Params, Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { LoadingController, ViewDidLeave, ViewWillEnter } from '@ionic/angular';
import {
  LOCAL_STORAGE_KEYS,
  PanAppState,
  TokenAuthMode,
  TokenAuthOptions,
  UsfTokenService,
  jwtDecode,
} from '@panamax/app-state';
import { FEATURES } from '@shared/constants/splitio-features';
import { Subscription } from 'rxjs';
import { filter, skip, take } from 'rxjs/operators';
import { PATHS } from '../shared/constants/paths';
import { LoginAnalyticsService } from '@app/login/analytics/login-analytics.service';

@Component({
  selector: 'app-oauth',
  templateUrl: './oauth.page.html',
  styleUrls: ['./oauth.page.scss'],
})
export class OauthPage implements ViewWillEnter, ViewDidLeave {
  profileRedirectFlagSub$: Subscription = new Subscription();
  private profileRedirectFlag = false;
  loader: HTMLIonLoadingElement;
  queryParams: Params;

  constructor(
    private loadingController: LoadingController,
    private router: Router,
    private panAppState: PanAppState,
    private tokenService: UsfTokenService,
    private loginAnalyticsService: LoginAnalyticsService,
  ) {
    this.profileRedirectFlagSub$ = this.panAppState
      .feature$([FEATURES.split_global_profile_redirect.name])
      .subscribe(enabled => (this.profileRedirectFlag = enabled));
  }

  async ionViewWillEnter() {
    this.presentLoading();

    await this.tokenService
      .handleB2CCallback(this.router.url)
      .then(
        async resp => {
          console.log('[OauthPage] Retriveing B2C auth connect response', resp);

          if (!resp?.idToken) {
            throw new Error(
              '[OauthPage] ERROR: No valid B2C authentication session found',
            );
          }

          const jwt = jwtDecode(resp.idToken);
          const enableKmsi = jwt.kmsi === 'true';
          const tokenAuthOptions: TokenAuthOptions = {
            idToken: resp.idToken,
            platform: Capacitor.isNativePlatform() ? 'MOBILE' : 'DESKTOP',
            kmsi: enableKmsi,
          };

          //Track if is new user
          if (jwt?.newMoxeUser) {
            try {
              this.loginAnalyticsService.trackNewUserSignup();
            } catch (e) {
              console.log(
                '[OauthPage] ERROR: Error during tracking new user signup',
                e,
              );
            }
          }

          try {
            // authorize currently has logic for navigation in error cases
            await this.tokenService.authorize(
              TokenAuthMode.B2C,
              tokenAuthOptions,
            );
            console.log('[OauthPage] Navigating to home or profile page');
            this.navigate();
          } catch (e) {
            console.log('[OauthPage] ERROR: Error during authorization', e);
          }
        },
        e => this.handleB2CError(e),
      )
      .catch(e => {
        console.error(
          '[OauthPage] ERROR: Problem occurred during B2C authentication',
          e,
        );
        this.router.navigate([PATHS.LOGIN_ERROR], {
          queryParams: { errorStatus: e?.status },
        });
        console.log(
          '***** [OauthPage ionViewWillEnter()] NAVIGATED TO ERROR PAGE CALLED',
        );
      });

    this.dismissLoading();
  }

  private navigate() {
    let r4params =
      JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.params)) || {};
    //handle retrieve deep link navigation params
    if (r4params?.returnUrl) {
      const queryParams = { ...r4params };
      delete queryParams.returnUrl;
      this.router.navigate([r4params.returnUrl], {
        queryParams: queryParams,
      });
      localStorage.removeItem(LOCAL_STORAGE_KEYS.params);
    } else {
      if (this.profileRedirectFlag) {
        this.panAppState.user$
          .pipe(
            filter(user => !!user),
            skip(1),
            take(1),
          )
          .subscribe(user => {
            if (!user.email) {
              this.router.navigate([PATHS.PROFILE]);
            } else {
              this.router.navigate([PATHS.HOME]);
            }
          });
      } else {
        this.router.navigate([PATHS.HOME]);
      }
    }
  }

  // Note: taken from auth-connect service. Not ideal, but token service is owning this for flexibility w/ error handling.
  //consider further refactor for better separation of concerns.
  async handleB2CError(error: any): Promise<void> {
    // If the error doesn't contain a message, we're not going to handle it here
    if (!typeof (error?.message === 'string')) {
      console.error('An unspecified error has occurred during login', error);
      this.router.navigate([PATHS.LOGIN_ERROR], {
        queryParams: { errorStatus: error?.status },
      });
      console.log(
        '***** [OauthPage handleB2CError() message not string] NAVIGATED TO ERROR PAGE CALLED',
      );
      return;
    }
    if (error.message.startsWith('AAD_Custom_Error_SessionRevoked')) {
      await this.tokenService.logout();
      return;
    }
    if (error.message.startsWith('AAD_Custom_Error_ExpiredLoginPage')) {
      await this.tokenService.logout();
      return;
    }
    // TODO: validate this is needed.  appears to be copied from the Ionic docs
    // Handle the password reset case for Azure AD
    if (error.message.startsWith('AADB2C90118')) {
      await this.tokenService.logout();
      return;
    }
    if (error.message.startsWith('No session data stored')) {
      await this.tokenService.logout();
      return;
    }

    if (
      error.message.startsWith('AAD_Custom_Error_RequirePasswordAndSkipMfa')
    ) {
      console.log('[OauthPage] Navigating to login for automation');
      this.router.navigate([PATHS.LOGIN], {
        queryParams: { automation: true },
      });
      return;
    }

    console.error(
      '[OauthPage] ERROR: An unknown error has occurred during login' + '\n',
      error,
    );
    this.router.navigate([PATHS.LOGIN_ERROR], {
      queryParams: { errorStatus: error?.status },
    });
    console.log(
      '***** [OauthPage handleB2CError() end of func] NAVIGATED TO ERROR PAGE CALLED',
    );
  }

  async presentLoading() {
    this.loader = await this.loadingController.create({
      message: 'Logging in and fetching data...',
      spinner: 'circles',
      duration: 3000,
    });
    try {
      await this.loader.present();
    } catch (err) {
      console.error(err);
    }
  }

  async dismissLoading() {
    this.loader = await this.loadingController.getTop();
    if (this.loader) {
      await this.loader.dismiss();
    }
  }

  async ionViewDidLeave() {
    if (this.profileRedirectFlagSub$) {
      this.profileRedirectFlagSub$.unsubscribe();
    }
  }
}
